import React, { useState } from 'react';
import { motion, useMotionValue, useTransform, useSpring } from 'framer-motion';
import { ArrowRight, Sparkles, TrendingUp, FileCheck, Calculator, Building2 } from 'lucide-react';

// Card com efeito 3D e parallax
const FeatureCard = ({ title, description, image, link, linkText, icon: Icon, index, isImageLeft }) => {
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const rotateX = useTransform(y, [-100, 100], [10, -10]);
  const rotateY = useTransform(x, [-100, 100], [-10, 10]);

  const springConfig = { stiffness: 300, damping: 30 };
  const rotateXSpring = useSpring(rotateX, springConfig);
  const rotateYSpring = useSpring(rotateY, springConfig);

  const handleMouseMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    x.set(event.clientX - centerX);
    y.set(event.clientY - centerY);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  const navigateTo = (url) => {
    window.location.href = url;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        rotateX: rotateXSpring,
        rotateY: rotateYSpring,
        transformStyle: 'preserve-3d',
      }}
      className="group relative bg-white rounded-3xl overflow-hidden cursor-pointer"
      onClick={() => navigateTo(link)}
    >
      {/* Glow effect de fundo */}
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(16, 185, 129, 0.1) 0%, transparent 70%)',
        }}
      />

      {/* Border gradient animada */}
      <div 
        className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{
          padding: '2px',
          background: 'linear-gradient(135deg, #10b981, #06b6d4, #10b981)',
          backgroundSize: '200% 200%',
          animation: 'gradientMove 3s ease infinite',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
        }}
      />

      <div className={`relative flex flex-col ${isImageLeft ? 'md:flex-row-reverse' : 'md:flex-row'} min-h-[280px]`}>
        {/* Conteúdo de texto */}
        <div className="w-full md:w-1/2 p-8 flex flex-col justify-center relative z-10">
          {/* Ícone animado */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            whileInView={{ scale: 1, rotate: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.15 + 0.3, type: 'spring', stiffness: 200 }}
            className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 relative"
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              boxShadow: '0 8px 25px rgba(16, 185, 129, 0.35)',
            }}
          >
            <Icon className="text-white" size={26} />
            {/* Pulse effect */}
            <motion.div
              className="absolute inset-0 rounded-2xl"
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              }}
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.5, 0, 0.5],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
          </motion.div>

          {/* Título com animação de texto */}
          <motion.h3 
            className="text-2xl font-bold text-[#2C5E50] mb-3 group-hover:text-[#10b981] transition-colors duration-300"
          >
            {title}
          </motion.h3>

          {/* Descrição */}
          <p className="text-gray-600 mb-6 leading-relaxed">
            {description}
          </p>

          {/* Botão CTA */}
          <motion.button 
            className="inline-flex items-center gap-2 text-[#10b981] font-semibold group/btn"
            whileHover={{ x: 5 }}
          >
            <span className="relative">
              {linkText}
              <span 
                className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#10b981] group-hover/btn:w-full transition-all duration-300"
              />
            </span>
            <motion.span
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <ArrowRight size={18} />
            </motion.span>
          </motion.button>
        </div>

        {/* Imagem com efeitos */}
        <div className="w-full md:w-1/2 relative overflow-hidden">
          {/* Overlay gradient */}
          <div 
            className={`absolute inset-0 z-10 opacity-30 group-hover:opacity-10 transition-opacity duration-500 ${
              isImageLeft 
                ? 'bg-gradient-to-l from-transparent to-white' 
                : 'bg-gradient-to-r from-transparent to-white'
            }`}
          />
          
          {/* Container da imagem */}
          <motion.div 
            className="relative h-full min-h-[250px] md:min-h-full"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <img 
              src={image} 
              alt={title}
              className="absolute inset-0 w-full h-full object-cover"
            />
            
            {/* Shine effect on hover */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out"
            />
          </motion.div>

          {/* Decoração flutuante */}
          <motion.div
            className="absolute top-4 right-4 z-20"
            animate={{ y: [0, -10, 0], rotate: [0, 5, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          >
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{
                background: 'rgba(255, 255, 255, 0.9)',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
              }}
            >
              <Sparkles size={18} className="text-[#10b981]" />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Estilos CSS inline para animação do gradiente */}
      <style jsx>{`
        @keyframes gradientMove {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </motion.div>
  );
};

const NextStepsSection = () => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const navigateTo = (url) => {
    window.location.href = url;
  };

  const features = [
    {
      title: 'Gestão Completa do seu CNPJ',
      description: 'Gerencie Guias, DAS, emita Notas Fiscais, receba lembretes, consulte benefícios INSS e cartão CNPJ em um só lugar.',
      image: 'https://res.cloudinary.com/dlk1ta1fr/image/upload/v1741556584/im-next-steps-1_fu03tv.png',
      link: 'https://app.meumei.net/register',
      linkText: 'Cadastre-se grátis',
      icon: Building2,
    },
    {
      title: 'Seu MEI em dia',
      description: 'Precisa regularizar seu CNPJ MEI? Descubra se você tem pepinos para resolver e saiba o que fazer.',
      image: 'https://res.cloudinary.com/dlk1ta1fr/image/upload/v1741564335/Leonardo_Kino_XL_create_an_image_where_an_older_white_man_is_l_3_kzapfx.jpg',
      link: 'https://app.meumei.net/register',
      linkText: 'Consultar MEI grátis',
      icon: FileCheck,
    },
    {
      title: 'Para o seu negócio crescer',
      description: 'Nosso App faz seu MEI crescer, sem esquecer do seu DAS e dos Limites de Faturamento.',
      image: 'https://res.cloudinary.com/dlk1ta1fr/image/upload/v1741564438/Leonardo_Kino_XL_create_an_image_where_an_older_white_man_is_l_0_tmiu6i.jpg',
      link: 'https://app.meumei.net/register',
      linkText: 'Crescer mais',
      icon: TrendingUp,
    },
    {
      title: 'Seu MEI em dia',
      description: 'Não esqueça do seu Imposto . Mantenha suas obrigações fiscais em dia com facilidade, sistema de alertas automaticos.',
      image: 'https://res.cloudinary.com/dlk1ta1fr/image/upload/v1741564659/Leonardo_Kino_XL_create_an_image_where_a_hardworking_black_man_2_jgrd2t.jpg',
      link: 'https://app.meumei.net/register',
      linkText: 'DAS Online',
      icon: Calculator,
    },
  ];

  return (
    <section 
      id="servicos-mei"
      className="relative py-24 overflow-hidden bg-gradient-to-b from-white via-gray-50/50 to-white scroll-mt-24"
    >
      {/* Elementos decorativos de fundo */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Círculos gradientes */}
        <motion.div
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -top-40 -right-40 w-96 h-96 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(16, 185, 129, 0.15) 0%, transparent 70%)',
          }}
        />
        <motion.div
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(16, 185, 129, 0.1) 0%, transparent 70%)',
          }}
        />

        {/* Grid pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(16, 185, 129, 0.5) 1px, transparent 1px),
              linear-gradient(90deg, rgba(16, 185, 129, 0.5) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Header da seção */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          {/* Badge animado */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
            style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(6, 182, 212, 0.1) 100%)',
              border: '1px solid rgba(16, 185, 129, 0.2)',
            }}
          >
            <motion.span
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
            >
              <Sparkles size={16} className="text-[#10b981]" />
            </motion.span>
            <span className="text-sm font-medium text-[#10b981]">
              Comece sua jornada
            </span>
          </motion.div>

          {/* Título principal */}
          <h2 className="text-4xl md:text-5xl font-bold text-[#2C5E50] mb-6">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              QUAL O PRÓXIMO PASSO
            </motion.span>
            <br />
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="bg-gradient-to-r from-[#10b981] to-[#06b6d4] bg-clip-text text-transparent"
            >
              DA SUA JORNADA?
            </motion.span>
          </h2>

          {/* Botão CTA principal */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigateTo('https://app.mei.oxygenhub.app/register')}
            className="relative mt-2 px-8 py-3 rounded-full font-semibold text-[#10b981] overflow-hidden group"
            style={{
              border: '2px solid #10b981',
            }}
          >
            {/* Background fill on hover */}
            <motion.span
              className="absolute inset-0 bg-[#10b981]"
              initial={{ x: '-100%' }}
              whileHover={{ x: 0 }}
              transition={{ duration: 0.3 }}
            />
            <span className="relative z-10 group-hover:text-white transition-colors duration-300">
              Cadastre-se gratuitamente
            </span>
          </motion.button>
        </motion.div>

        {/* Grid de cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {features.map((feature, index) => (
            <FeatureCard
              key={feature.title}
              {...feature}
              index={index}
              isImageLeft={index % 2 === 1}
            />
          ))}
        </div>

        {/* CTA final */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="mt-16 text-center"
        >
          <p className="text-gray-600 mb-6">
            Junte-se <span className="font-bold text-[#10b981]">aos</span> MEIs que escolheram o MeuMei
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigateTo('https://app.meumei.net/register')}
            className="inline-flex items-center gap-3 px-10 py-4 text-white font-semibold rounded-full"
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              boxShadow: '0 10px 40px rgba(16, 185, 129, 0.35)',
            }}
          >
            <span>Começar agora</span>
            <motion.span
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <ArrowRight size={20} />
            </motion.span>
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default NextStepsSection;